import { StripePlansSchema } from "server/schemas/user";
import { z } from "zod";

// IMPORTANT: Plan is determined by the payment link used during checkout!
// See https://dashboard.stripe.com/products/prod_MqjHatiSAr8ojh

interface StripePlanInfo {
  [key: string]: {
    name: string;
    fullName: string;
    price: number;
    interval: string;
  };
}
export const planInfo: StripePlanInfo = {
  price_1NQskxANrorjxqsOIt3Vv2fd: {
    name: "Premium",
    fullName: "Interfaces Premium (Annual)",
    price: 220,
    interval: "yr",
  },
  price_1NQsjgANrorjxqsOfHxQlF5a: {
    name: "Premium",
    fullName: "Interfaces Premium (Monthly)",
    price: 20,
    interval: "mo",
  },
  price_1NgTUcANrorjxqsOALqgSbQs: {
    // Separate Stripe product: https://dashboard.stripe.com/products/prod_OTQLLfwBmNG7rU
    name: "Advanced",
    fullName: "Interfaces Advanced (Monthly)",
    price: 100,
    interval: "mo",
  },
  price_1NgThTANrorjxqsOIlwwhTP0: {
    // Separate Stripe product: https://dashboard.stripe.com/products/prod_OTQLLfwBmNG7rU
    name: "Advanced",
    fullName: "Interfaces Advanced (Annual)",
    price: 1200,
    interval: "yr",
  },
  price_1NLSOwANrorjxqsOGKcQulr3: {
    // Legacy - same Stripe product as Premium
    name: "Advanced",
    fullName: "Interfaces Advanced (Monthly) - Legacy",
    price: 100,
    interval: "mo",
  },
  price_1NLSL4ANrorjxqsOM5tdu2hl: {
    // Legacy - same Stripe product as Premium
    name: "Advanced",
    fullName: "Interfaces Advanced (Annual) - Legacy",
    price: 1200,
    interval: "yr",
  },
  price_1MTrYOANrorjxqsO52f7Z4aX: {
    name: "Premium (Legacy)",
    fullName: "Interfaces Premium (Annual) - Legacy",
    price: 220,
    interval: "yr",
  },
  price_1M71oeANrorjxqsOaLNWuuoR: {
    name: "Premium (Legacy)",
    fullName: "Interfaces Premium (Monthly) - Legacy",
    price: 20,
    interval: "mo",
  },
};

export const legacyPlans = [
  "price_1M71oeANrorjxqsOaLNWuuoR",
  "price_1MTrYOANrorjxqsO52f7Z4aX",
];

export const premiumPlans = [
  "price_1NQskxANrorjxqsOIt3Vv2fd",
  "price_1NQsjgANrorjxqsOfHxQlF5a",
];

export const advancedPlans = [
  // Legacy plans on old Stripe product
  "price_1NLSOwANrorjxqsOGKcQulr3",
  "price_1NLSL4ANrorjxqsOM5tdu2hl",
  // Current plans on a separate Stripe product from the Premium plans
  "price_1NgTUcANrorjxqsOALqgSbQs",
  "price_1NgThTANrorjxqsOIlwwhTP0",
];

export const isLegacy = (planId?: string | null) => {
  return planId && legacyPlans.includes(planId);
};

export const isPremium = (planId?: string | null) => {
  return planId && premiumPlans.includes(planId);
};

export const isPremiumOrLegacyPremium = (planId?: string | null) => {
  return planId && (isPremium(planId) || isLegacy(planId));
};

export const isAdvanced = (planId?: string | null) => {
  return planId && advancedPlans.includes(planId);
};

const paidFeatures = z.enum([
  "customColors",
  "branding",
  "embedding",
  "customDomain",
  "analytics",
  "customTokenLength",
  "passwordProtect",
  "managedUsers",
  "chatBotDataSources",
  "hideZapierButton",
  "dynamicFilters",
  "collaborators",
]);

export type PaidFeature = z.infer<typeof paidFeatures>;

const legacyFeatures: PaidFeature[] = [
  "customColors",
  "branding",
  "embedding",
  "customDomain",
  "analytics",
  "customTokenLength",
  "passwordProtect",
  "managedUsers",
  "chatBotDataSources",
  "hideZapierButton",
  "dynamicFilters",
];

const premiumFeatures: PaidFeature[] = [
  "customColors",
  "branding",
  "embedding",
  "analytics",
  "customTokenLength",
  "passwordProtect",
  "managedUsers",
  "chatBotDataSources",
  "hideZapierButton",
];

const advancedFeatures: PaidFeature[] = [
  ...premiumFeatures,
  "customDomain",
  "dynamicFilters",
  "collaborators",
];

export const userHasAccess = (
  planId?: string | null,
  isStaff?: boolean,
  feature?: PaidFeature
) => {
  if (isStaff) {
    return true;
  }

  if (!planId) return false;

  const validPlan = StripePlansSchema.safeParse(planId);

  if (!validPlan.success) return false;

  // Allows us to use this function to check if a user has a plan for readability
  // Some features aren't explicitly defined yet
  if (!feature) return true;

  if (isLegacy(planId)) {
    return legacyFeatures.includes(feature);
  }

  if (isPremium(planId)) {
    return premiumFeatures.includes(feature);
  }

  if (isAdvanced(planId)) {
    return advancedFeatures.includes(feature);
  }

  return false;
};

const tieredFeatures = z.enum(["projectCount"]);

type TieredFeature = z.infer<typeof tieredFeatures>;

interface TieredFeatureConfig {
  [feature: string]: {
    free: number;
    premium: number;
    advanced: number;
  };
}

const tieredFeaturesConfig: TieredFeatureConfig = {
  projectCount: {
    free: 2,
    premium: 5,
    advanced: Number.POSITIVE_INFINITY,
  },
};

export const getTieredFeatureLimit = (
  feature: TieredFeature,
  planId?: string | null
) => {
  if (!planId) return tieredFeaturesConfig[feature].free;

  if (isLegacy(planId)) {
    return tieredFeaturesConfig[feature].premium;
  }

  if (isPremium(planId)) {
    return tieredFeaturesConfig[feature].premium;
  }

  if (isAdvanced(planId)) {
    return tieredFeaturesConfig[feature].advanced;
  }

  return tieredFeaturesConfig[feature].free;
};
